import { axiosWithoutToken, axiosWithToken } from "@/utils/axios";

export type ConsentChoice =
  | "agree_and_proceed"
  | "decline_all"
  | "essentials_only"
  | "custom"
  | "withdrawn"
  | "auto_opt_in";

export interface PostConsentPayload {
  user_id?: string;
  guest_id?: string;
  consent: boolean | null;
  choice?: ConsentChoice;
  categories?: {
    analytics?: boolean;
    marketing?: boolean;
  };
}

export interface PostConsentResult {
  ok: boolean;
  consent?: boolean | null;
  guest_id?: string;
}

/**
 * Guest-capable consent sync. Uses axiosWithoutToken (withCredentials:true)
 * so the backend can set/refresh bnp_guest_id + consent cookies even for
 * anonymous visitors. Failures are silent by design — consent UX must never
 * break the page.
 */
export async function postConsent(
  payload: PostConsentPayload,
): Promise<PostConsentResult> {
  try {
    const res = await axiosWithoutToken.post("/user/accept_cookies", payload);
    return {
      ok: true,
      consent: res.data?.data?.consent ?? payload.consent,
      guest_id: res.data?.data?.guest_id ?? payload.guest_id,
    };
  } catch {
    return { ok: false };
  }
}

export interface ConsentSettings {
  consent: boolean | null;
  choice: ConsentChoice | string | null;
  categories: { analytics?: boolean; marketing?: boolean } | null;
  consent_at: string | null;
}

/**
 * Fetch the signed-in user's persisted cookie settings (backend is the source
 * of truth once authenticated). The backend also refreshes the httpOnly
 * consent cookies on this response. Silent-fail: null means keep local state.
 */
export async function getConsent(): Promise<{
  ok: boolean;
  settings: ConsentSettings | null;
}> {
  try {
    const res = await axiosWithToken().get("/user/cookie_consent");
    return { ok: true, settings: (res.data?.data ?? null) as ConsentSettings | null };
  } catch {
    return { ok: false, settings: null };
  }
}

const consentService = { postConsent, getConsent };
export default consentService;
